import { useAuth } from 'react-oidc-context';
import { ShieldX, LogIn } from 'lucide-react';
import { Button, Card } from '@verif-y/ui';
import { AppFooter } from '@/components/layout/app-footer';

/**
 * Unauthorized Page Component
 * 
 * Displays when a user lacks the required role to access the application.
 * Provides a clear message and option to return to login.
 */
const Unauthorized = () => {
  const auth = useAuth();

  const handleReturnToLogin = async () => {
    try {
      await auth.removeUser();
      await auth.signoutRedirect();
    } catch (error) {
      console.error('Logout failed:', { errorCode: 'LOGOUT_FAIL' });
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Main Content */}
      <main
        role="main"
        aria-labelledby="unauthorized-title"
        className="flex-1 flex items-center justify-center p-4"
      >
        <Card
          variant="glass"
          className="p-8 max-w-md w-full text-center"
          role="alert"
          aria-live="polite"
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-red-100">
              <ShieldX
                size={48}
                className="text-red-600"
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Title */}
          <h1
            id="unauthorized-title"
            className="text-2xl font-bold text-foreground mb-4"
          >
            Access Denied
          </h1>

          {/* Message */}
          <p className="text-muted-foreground mb-6 leading-relaxed">
            You are not authorized to access this application. 
            This requires appropriate user privileges.
          </p>

          <p className="text-sm text-muted-foreground/80 mb-8">
            If you believe this is an error, please contact your system administrator 
            to request the appropriate access permissions.
          </p>

          {/* Return to Login Button */}
          <Button
            onClick={handleReturnToLogin}
            aria-label="Return to login page"
          >
            <LogIn size={20} aria-hidden="true" />
            Return to Login
          </Button>
        </Card>
      </main>

      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default Unauthorized;
